import {
  CommonHelperFunctions,
  ICreateLibraryRequest,
  ICreatePackageRepositoryRequest,
  ILibraryResponse,
  IPackageRepositoryResponse,
  IUpdateLibraryRequest,
  IUpdatePackageRepositoryRequest,
  PackageType
} from "@kuika/kuika-cl-model";
import {
  INpmPackage,
  INpmSearchResponse,
  INuGetPackage,
  INuGetSearchResponse
} from "@kuika/kuika-cl-model/src/models/package-management";
import Axios, { AxiosResponse } from "axios";
import { KMainFunctions } from "../shared/utility/main-functions";

const backendUrl = CommonHelperFunctions.GetBackendUrl();

export class PackageManagementService {
  public static async GetRepositories(
    domainId?: string,
    projectId?: string,
    packageType?: PackageType
  ): Promise<AxiosResponse<IPackageRepositoryResponse[]>> {
    try {
      let url = `${backendUrl}/packagemanagement/repositories`;
      const params: Record<string, string> = {};

      if (domainId) params.domainId = domainId;
      if (projectId) params.projectId = projectId;
      if (packageType !== undefined) params.packageType = packageType.toString();

      return await Axios.get<null, AxiosResponse>(url, { params });
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async GetRepository(id: string): Promise<AxiosResponse<IPackageRepositoryResponse>> {
    try {
      return await Axios.get<null, AxiosResponse>(`${backendUrl}/packagemanagement/repositories/${id}`);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async CreateRepository(
    request: ICreatePackageRepositoryRequest
  ): Promise<AxiosResponse<IPackageRepositoryResponse>> {
    try {
      return await Axios.post<null, AxiosResponse>(`${backendUrl}/packagemanagement/repositories`, request);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async UpdateRepository(
    id: string,
    request: IUpdatePackageRepositoryRequest
  ): Promise<AxiosResponse<void>> {
    try {
      return await Axios.put<null, AxiosResponse>(`${backendUrl}/packagemanagement/repositories/${id}`, request);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async DeleteRepository(id: string): Promise<AxiosResponse<void>> {
    try {
      return await Axios.delete<null, AxiosResponse>(`${backendUrl}/packagemanagement/repositories/${id}`);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async GetDefaultRepository(
    packageType: PackageType
  ): Promise<AxiosResponse<IPackageRepositoryResponse>> {
    try {
      return await Axios.get<null, AxiosResponse>(
        `${backendUrl}/packagemanagement/repositories/default/${packageType}`
      );
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async GetProjectRepository(
    projectId: string,
    packageType: PackageType
  ): Promise<AxiosResponse<IPackageRepositoryResponse>> {
    try {
      return await Axios.get<null, AxiosResponse>(
        `${backendUrl}/packagemanagement/repositories/project/${projectId}/${packageType}`
      );
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async GetLibraries(
    domainId?: string,
    projectId?: string,
    packageType?: PackageType,
    isActive?: boolean
  ): Promise<AxiosResponse<ILibraryResponse[]>> {
    try {
      let url = `${backendUrl}/packagemanagement/libraries`;
      const params: Record<string, string> = {};

      if (domainId) params.domainId = domainId;
      if (projectId) params.projectId = projectId;
      if (packageType !== undefined) params.packageType = packageType.toString();
      if (isActive !== undefined) params.isActive = isActive.toString();

      return await Axios.get<null, AxiosResponse>(url, { params });
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async GetLibrary(id: string): Promise<AxiosResponse<ILibraryResponse>> {
    try {
      return await Axios.get<null, AxiosResponse>(`${backendUrl}/packagemanagement/libraries/${id}`);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async CreateLibrary(request: ICreateLibraryRequest): Promise<AxiosResponse<ILibraryResponse>> {
    try {
      return await Axios.post<null, AxiosResponse>(`${backendUrl}/packagemanagement/libraries`, request);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async UpdateLibrary(id: string, request: IUpdateLibraryRequest): Promise<AxiosResponse<void>> {
    try {
      return await Axios.put<null, AxiosResponse>(`${backendUrl}/packagemanagement/libraries/${id}`, request);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async DeleteLibrary(id: string): Promise<AxiosResponse<void>> {
    try {
      return await Axios.delete<null, AxiosResponse>(`${backendUrl}/packagemanagement/libraries/${id}`);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async GetLibrariesByDomain(
    domainId: string,
    packageType?: PackageType,
    active?: boolean
  ): Promise<AxiosResponse<ILibraryResponse[]>> {
    try {
      let url = `${backendUrl}/packagemanagement/libraries/domain/${domainId}`;
      const params: Record<string, string> = {};

      if (packageType !== undefined) params.packageType = packageType.toString();
      if (active !== undefined) params.active = active.toString();

      return await Axios.get<null, AxiosResponse>(url, { params });
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async GetLibrariesByProject(
    projectId: string,
    packageType?: PackageType,
    active?: boolean
  ): Promise<AxiosResponse<ILibraryResponse[]>> {
    try {
      let url = `${backendUrl}/packagemanagement/libraries/project/${projectId}`;
      const params: Record<string, string> = {};

      if (packageType !== undefined) params.packageType = packageType.toString();
      if (active !== undefined) params.active = active.toString();

      return await Axios.get<null, AxiosResponse>(url, { params });
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async ListNuGetPackages(
    searchQuery: string = "",
    skip: number = 0,
    take: number = 20,
    excludeDefaultPackages: boolean = true
  ): Promise<AxiosResponse<INuGetSearchResponse>> {
    try {
      const params = {
        searchQuery,
        skip: skip.toString(),
        take: take.toString(),
        excludeDefaultPackages: excludeDefaultPackages.toString()
      };

      return await Axios.get<null, AxiosResponse>(`${backendUrl}/packagemanagement/nuget/packages`, {
        params
      });
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async ListNpmPackages(
    searchQuery: string = "",
    skip: number = 0,
    take: number = 20
  ): Promise<AxiosResponse<INpmSearchResponse>> {
    try {
      const params = {
        searchQuery,
        skip: skip.toString(),
        take: take.toString()
      };

      return await Axios.get<null, AxiosResponse>(`${backendUrl}/packagemanagement/npm/packages`, {
        params
      });
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async GetNuGetPackageDetails(
    packageId: string,
    version?: string
  ): Promise<AxiosResponse<INuGetPackage>> {
    try {
      const url = version
        ? `${backendUrl}/packagemanagement/nuget/packages/${packageId}/${version}`
        : `${backendUrl}/packagemanagement/nuget/packages/${packageId}`;

      return await Axios.get<null, AxiosResponse>(url);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async ListPyPIPackages(
    searchQuery: string = "",
    skip: number = 0,
    take: number = 20,
    excludeDefaultPackages: boolean = true
  ): Promise<AxiosResponse<INuGetSearchResponse>> {
    try {
      const params = {
        searchQuery,
        skip: skip.toString(),
        take: take.toString(),
        excludeDefaultPackages: excludeDefaultPackages.toString()
      };

      return await Axios.get<null, AxiosResponse>(`${backendUrl}/packagemanagement/pypi/packages`, {
        params
      });
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async GetPyPIPackageDetails(
    packageId: string,
    version?: string
  ): Promise<AxiosResponse<INuGetPackage>> {
    try {
      const url = version
        ? `${backendUrl}/packagemanagement/pypi/packages/${packageId}/${version}`
        : `${backendUrl}/packagemanagement/pypi/packages/${packageId}`;

      return await Axios.get<null, AxiosResponse>(url);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async ListMavenPackages(
    searchQuery: string = "",
    skip: number = 0,
    take: number = 20,
    excludeDefaultPackages: boolean = true
  ): Promise<AxiosResponse<INuGetSearchResponse>> {
    try {
      const params = {
        searchQuery,
        skip: skip.toString(),
        take: take.toString(),
        excludeDefaultPackages: excludeDefaultPackages.toString()
      };

      return await Axios.get<null, AxiosResponse>(`${backendUrl}/packagemanagement/maven/packages`, {
        params
      });
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async GetMavenPackageDetails(
    packageId: string,
    version?: string
  ): Promise<AxiosResponse<INuGetPackage>> {
    try {
      // packageId is the Maven coordinate "groupId:artifactId" — pass via query param (colon in path is awkward).
      const params: Record<string, string> = { packageId };
      if (version) params.version = version;

      return await Axios.get<null, AxiosResponse>(`${backendUrl}/packagemanagement/maven/package`, { params });
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async GetNpmPackageDetails(packageId: string, version?: string): Promise<AxiosResponse<INpmPackage>> {
    try {
      const url = version
        ? `${backendUrl}/packagemanagement/npm/packages/${packageId}/${version}`
        : `${backendUrl}/packagemanagement/npm/packages/${packageId}`;

      return await Axios.get<null, AxiosResponse>(url);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }
}
